import { bannersApi, type Banner } from '@/api';
import { logger } from '@/utils/logger';
import { useState, useEffect } from 'react';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router';
import { scrollToTopAndNavigate } from '../utils/navigationHelpers';
import { useNavigation } from '../contexts/NavigationContext';


export function ConferenceBanner() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [dismissedBanners, setDismissedBanners] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { navigationStarted } = useNavigation();

  useEffect(() => {
    // Load dismissed banners from localStorage
    const dismissed = localStorage.getItem('dismissed_banners');
    if (dismissed) {
      try {
        const parsedDismissed = JSON.parse(dismissed);
        logger.debug('🎪 Dismissed banners from localStorage:', parsedDismissed);
        setDismissedBanners(parsedDismissed);
      } catch (e) {
        logger.error('Error parsing dismissed banners:', e);
      }
    } else {
      logger.debug('🎪 No dismissed banners in localStorage');
    }

    // Fetch active banners
    fetchActiveBanners();
  }, []);

  const fetchActiveBanners = async () => {
    try {
      logger.debug('🎪 Fetching active banners from API...');
      const { banners: active } = await bannersApi.active();
      setBanners(active ?? []);
    } catch (error) {
      // A banner is decoration; a failure here must not disturb the page.
      logger.error('Could not load banners:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissBanner = (bannerId: string) => {
    const newDismissed = [...dismissedBanners, bannerId];
    setDismissedBanners(newDismissed);
    localStorage.setItem('dismissed_banners', JSON.stringify(newDismissed));
  };

  const handleBannerClick = (link: string) => {
    dismissBanner(banner.id); // Dismiss the banner first
    scrollToTopAndNavigate(navigate, link, navigationStarted);
  };

  if (isLoading || banners.length === 0) {
    return null;
  }

  // Filter out dismissed banners
  const visibleBanners = banners.filter(banner => !dismissedBanners.includes(banner.id));
  logger.debug('🎪 Visible banners after filtering:', visibleBanners.length);
  logger.debug('🎪 Dismissed banner IDs:', dismissedBanners);

  if (visibleBanners.length === 0) {
    logger.debug('🎪 No visible banners - all dismissed or none active');
    return null;
  }

  // Show the first visible banner
  const banner = visibleBanners[0];
  logger.debug('🎪 Showing banner:', banner.id, banner.title);

  return (
    <>
      {/* Backdrop Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] animate-in fade-in duration-300"
        onClick={() => dismissBanner(banner.id)}
      />

      {/* Centered Popup Modal */}
      <div data-site-popup="" className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative bg-gradient-to-br from-rose-600 via-pink-700 to-fuchsia-800 text-white rounded-2xl shadow-2xl max-w-2xl w-full pointer-events-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-500"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-20 overflow-hidden rounded-2xl">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-amber-300 rounded-full filter blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-300 rounded-full filter blur-3xl"></div>
          </div>

          {/* Close Button - Top Right */}
          <button
            onClick={() => dismissBanner(banner.id)}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10 group"
            aria-label="Close popup"
          >
            <X className="h-6 w-6 group-hover:rotate-90 transition-transform duration-300" />
          </button>

          {/* Content */}
          <div className="relative p-8 md:p-10">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-amber-300 text-rose-900 shadow-lg animate-pulse">
                <Sparkles className="h-10 w-10" />
              </div>
            </div>

            {/* Type Badge */}
            <div className="flex justify-center mb-4">
              <span className="inline-block px-4 py-1.5 rounded-full bg-amber-300 text-rose-900 text-sm font-bold shadow-md">
                We are AI testing experts
              </span>
            </div>

            {/* Title */}
            <div className="text-center mb-4">
              <h2 className="text-2xl md:text-3xl font-bold inline-block">
                {banner.title}
              </h2>
            </div>

            {/* Message */}
            <p className="text-center text-base md:text-lg text-pink-50 mb-3 leading-relaxed">
              {banner.message}
            </p>

            {/* Description */}
            <p className="text-center text-sm text-pink-100 mb-8 leading-relaxed">
              {banner.description}
            </p>

            {/* CTA Button */}
            <div className="flex justify-center">
              <button
                onClick={() => handleBannerClick(banner.link)}
                className="group flex items-center gap-3 px-8 py-4 rounded-xl bg-amber-300 hover:bg-amber-400 text-rose-900 font-bold text-lg transition-all shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95"
              >
                <span>{banner.linkText}</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Bottom Border Accent */}
          <div className="h-2 bg-gradient-to-r from-amber-300 via-amber-400 to-amber-300 rounded-b-2xl"></div>
        </div>
      </div>
    </>
  );
}